import React, { FC, useEffect, useState } from 'react'
import * as ModalStyles from '../../../../../ModalWindow/modal.styles'
import * as LocalStyles from '../../styles'
import { RelevantQuestionButtons } from '../sub_question/buttons/relevant-question-button'
import { TrainingQuestionButtons } from '../sub_question/buttons/training-question-buttons'
import { MainQuestionButton } from '../sub_question/buttons/main-question-button'
import { QuestionProps } from '../types'

type Layout = 'horizontal' | 'vertical'

interface GenericQuestionProps extends QuestionProps {
  questionLabel: string
  answerLabel: string
  layout: Layout
}

export const BaseTrainedQuestion: FC<GenericQuestionProps> = ({
  question: inputQuestion,
  answer: inputAnswer,
  questionId,
  questionType,
  deleteTrainingQuestionCallback,
  updateQuestionCallback,
  createQuestionCallback,
  onQuestionWasChanged,
  questionLabel,
  answerLabel,
  layout,
  initQuestionType,
}) => {
  const [question, setQuestion] = useState(inputQuestion)
  const [answer, setAnswer] = useState(inputAnswer)
  const [selectedQuestionType, setSelectedQuestionType] = useState<string>(initQuestionType)
  const [buttonIsActive, setButtonIsActive] = useState<boolean>(false)
  const [savedQuestionType, setSavedQuestionType] = useState<string>(initQuestionType)

  function checkQuestionTypeIsChanged() {
    if (!selectedQuestionType) return false
    return savedQuestionType !== selectedQuestionType
  }

  function changeButtonStatus() {
    const changed = inputQuestion !== question || inputAnswer !== answer
    const questionAndAnswerNotEmpty = Boolean(question) && Boolean(answer)
    const selectedQuestionTypeExist = Boolean(selectedQuestionType)
    const questionTypeIsChanged = checkQuestionTypeIsChanged()
    setButtonIsActive((changed || questionTypeIsChanged) && questionAndAnswerNotEmpty && selectedQuestionTypeExist)
    const questionWasChanged = changed || questionTypeIsChanged
    if (onQuestionWasChanged) {
      onQuestionWasChanged(questionWasChanged)
    }
  }

  useEffect(() => {
    changeButtonStatus()
  }, [question, answer, inputQuestion, inputAnswer, onQuestionWasChanged, selectedQuestionType])

  const handleQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value)
  }

  const handleAnswerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAnswer(e.target.value)
  }

  const handleDelete = () => {
    deleteTrainingQuestionCallback(questionId)
    setButtonIsActive(false)
  }

  const handleCreateNewQuestion = () => {
    createQuestionCallback(question, answer, selectedQuestionType)
    setButtonIsActive(false)
  }

  const handleUpdate = () => {
    updateQuestionCallback(questionId, inputQuestion, question, answer, selectedQuestionType)
    setSavedQuestionType(selectedQuestionType)
    setButtonIsActive(false)
  }

  const ButtonComponent = buttons[questionType]

  const isHorizontal = layout === 'horizontal'

  return (
    <>
      {isHorizontal ? (
        <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
          <ModalStyles.ModalContainerSectionItem style={{ flex: 1 }}>
            <ModalStyles.SubHeader>{questionLabel}</ModalStyles.SubHeader>
            <LocalStyles.AnswerInput as="textarea" value={question} onChange={handleQuestionChange} />
          </ModalStyles.ModalContainerSectionItem>

          <ModalStyles.ModalContainerSectionItem style={{ flex: 1 }}>
            <ModalStyles.SubHeader>{answerLabel}</ModalStyles.SubHeader>
            <LocalStyles.AnswerInput as="textarea" value={answer} onChange={handleAnswerChange} />
          </ModalStyles.ModalContainerSectionItem>
        </div>
      ) : (
        <>
          <ModalStyles.ModalContainerSectionItem>
            <ModalStyles.SubHeader>{'Question'}</ModalStyles.SubHeader>
            <LocalStyles.AnswerInput as="textarea" value={question} onChange={handleQuestionChange} />
          </ModalStyles.ModalContainerSectionItem>

          <ModalStyles.ModalContainerSectionItem style={{ marginTop: 16 }}>
            <ModalStyles.SubHeader>{'Answer'}</ModalStyles.SubHeader>
            <LocalStyles.AnswerInput
              as="textarea"
              value={answer}
              onChange={handleAnswerChange}
              style={{
                fontFamily: 'monospace',
                whiteSpace: 'pre',
                height: '250px',
              }}
            />
          </ModalStyles.ModalContainerSectionItem>
        </>
      )}

      <ButtonComponent
        onCreateNewQuestion={handleCreateNewQuestion}
        onUpdateQuestion={handleUpdate}
        buttonIsActive={buttonIsActive}
        onDelete={handleDelete}
        setSelectedQuestionType={setSelectedQuestionType}
        initQuestionType={initQuestionType}
      />
    </>
  )
}

const buttons = {
  relevant: RelevantQuestionButtons,
  trained: TrainingQuestionButtons,
  main: MainQuestionButton,
} as const
